import _ from 'lodash';
import { Message } from '@scale/shared/types/chat-completion';
import {
  IndexedInteractionStepResponse,
  MultiTurnInteractionConfiguration,
  TurnContinuationContext,
  InteractionStepType,
  IndexedInteractionStepResponseForType,
  ContextType,
  MessageRole,
  StepParamsForType,
  MultiTurnInteractionConfigurationV2,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import { extractPrefilledMessages } from '@scale/shared/chat/response/validation';
import { RESPONSE_GENERATOR_STEPS, ChatTaskParamsStages } from '@scale/shared/chat/steps';
import { ReviewLevel } from '@scale/shared-types';
import uuid from 'uuid';
import {
  StepWithResponse,
  IMultiTurnThreadHistory,
  TurnStepsWithResponses,
} from './ThreadHistory.interface';
import { producePromptWithReferenceTexts, isConditionallyVisible } from '@scale/shared/chat/api';

export class MultiTurnThreadHistory implements IMultiTurnThreadHistory {
  #promptInputStepId: string;

  constructor(
    public threadParams: MultiTurnInteractionConfiguration | MultiTurnInteractionConfigurationV2,
    public responses: IndexedInteractionStepResponse[],
    private reviewLevel?: ReviewLevel,
  ) {
    // Assuming there is only one prompt input step
    // Q: can we ensure there's always a promptInput step?
    // If so we can remove uuid.v4() here or throw an error if it's not found
    const promptInputStepId = threadParams.before.find(
      step => step.type === InteractionStepType.PromptInput,
    );
    this.#promptInputStepId = promptInputStepId?.id ?? uuid.v4();
  }

  until(index: number) {
    return new MultiTurnThreadHistory(
      this.threadParams,
      this.responses.slice(0, index),
      this.reviewLevel,
    );
  }

  createNewTurnSteps(): StepParamsForType<InteractionStepType>[] {
    return [
      ...this.threadParams.turn,
      {
        id: this.#promptInputStepId,
        type: InteractionStepType.MultiTurnContinue,
        params: {
          instructions: 'Continue with the prompt or end your session.',
        },
      },
    ];
  }

  getMostRecentResponse() {
    for (let ii = this.responses.length - 1; ii >= 0; ii--) {
      const response = this.responses[ii];
      if (
        response.contextType !== ContextType.Skipped &&
        RESPONSE_GENERATOR_STEPS.includes(response.type)
      ) {
        return response.output;
      }
      if (
        [InteractionStepType.PromptInput, InteractionStepType.MultiTurnContinue].includes(
          response.type,
        )
      ) {
        break;
      }
    }
    return '';
  }

  getMostRecentResponseFullObject() {
    for (let ii = this.responses.length - 1; ii >= 0; ii--) {
      const response = this.responses[ii];
      if (
        response.contextType !== ContextType.Skipped &&
        RESPONSE_GENERATOR_STEPS.includes(response.type)
      ) {
        return response;
      }
      if (
        [InteractionStepType.PromptInput, InteractionStepType.MultiTurnContinue].includes(
          response.type,
        )
      ) {
        break;
      }
    }
    return null;
  }

  getMostRecentPrompt() {
    for (let ii = this.responses.length - 1; ii >= 0; ii--) {
      if (
        this.responses[ii].type === InteractionStepType.PromptInput ||
        this.responses[ii].type === InteractionStepType.MultiTurnContinue
      ) {
        return producePromptWithReferenceTexts(
          this.responses[ii],
          this.threadParams.useAppendedReferenceText,
        );
      }
    }
    return '';
  }

  getMostRecentOutput() {
    if (this.responses.length === 0) {
      return '';
    }
    return this.responses[this.responses.length - 1].output;
  }

  getMostRecentOutputFullObject() {
    if (this.responses.length === 0) {
      return null;
    }
    return this.responses[this.responses.length - 1];
  }

  getResponseAtIndex(responseIndex: number) {
    if (responseIndex < 0 || responseIndex >= this.responses.length) {
      throw new RangeError(`Response index ${responseIndex} is out of range`);
    }
    return this.responses[responseIndex];
  }

  getMostRecentPromptIndex() {
    for (let ii = this.responses.length - 1; ii >= 0; ii--) {
      if (
        this.responses[ii].type === InteractionStepType.PromptInput ||
        this.responses[ii].type === InteractionStepType.MultiTurnContinue
      ) {
        return ii;
      }
    }
    return -1;
  }

  getMostRecentPromptAttachments() {
    const mostRecentPromptIndex = this.getMostRecentPromptIndex();
    if (mostRecentPromptIndex === -1) {
      return '';
    }
    const attachments = this.responses[mostRecentPromptIndex].context?.attachments;
    if (!attachments) {
      return [];
    }
    return attachments;
  }

  getMostRecentResponseForStepType<T extends InteractionStepType>(
    stepType: T,
  ): IndexedInteractionStepResponseForType<T> | null {
    const stepsWithResponses = this.getFlattenedStepsWithResponses();

    for (let ii = stepsWithResponses.length - 1; ii >= 0; ii--) {
      const [stepParams, response] = stepsWithResponses[ii];
      if (!response || !stepParams) {
        continue;
      }
      if (stepParams.type === stepType) {
        return response as IndexedInteractionStepResponseForType<T>;
      }
    }
    return null;
  }

  getAllResponsesForStepType<T extends InteractionStepType>(stepType: T) {
    const responses: IndexedInteractionStepResponseForType<T>[] = [];
    const stepsWithResponses = this.getFlattenedStepsWithResponses();

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let ii = 0; ii < stepsWithResponses.length; ii++) {
      const [params, response] = stepsWithResponses[ii];
      if (!response || !params) {
        continue;
      }
      if (params.type === stepType) {
        responses.push(response as IndexedInteractionStepResponseForType<T>);
      }
    }

    return responses;
  }

  getMostRecentStepParamsForStepType<T extends InteractionStepType>(
    stepType: T,
  ): StepParamsForType<T> | null {
    const stepsWithResponses = this.getFlattenedStepsWithResponses();

    for (let ii = stepsWithResponses.length - 2; ii >= 0; ii--) {
      const [stepParams] = stepsWithResponses[ii];
      if (!stepParams) {
        continue;
      }
      if (stepParams.type === stepType) {
        return stepParams as StepParamsForType<T>;
      }
    }
    return null;
  }

  // Match useMostRecentResponseInTurn in ChatStore.hooks.ts
  useMostRecentResponseInTurn = (
    turn: number,
    stepParams: StepParamsForType<InteractionStepType.ModelResponseSelector>,
  ): string => {
    // find valid indicies for turn
    const responses = this.responses;
    let previousTurnStartIndex = responses.length;
    let previousTurnEndIndex = -1;
    let numMTCSeen = 0;
    for (let i = 0; i < responses.length; i++) {
      const step = responses[i];
      if (step?.type === InteractionStepType.MultiTurnContinue) {
        numMTCSeen++;
      }
      if (numMTCSeen === turn) {
        previousTurnEndIndex = Math.max(previousTurnEndIndex, i);
        previousTurnStartIndex = Math.min(previousTurnStartIndex, i);
      }
    }
    const mostRecentResponseGeneratorStepInTurn = (
      this.responses as IndexedInteractionStepResponse[]
    )
      .filter((response, i) => {
        const stepParamsWithStage =
          stepParams as StepParamsForTypeWithStage<InteractionStepType.ModelResponseSelector>;

        // only need to add in turn when casting since conditionality only checks for turn
        stepParamsWithStage.turn = turn;
        return (
          i >= previousTurnStartIndex &&
          i <= previousTurnEndIndex &&
          response.contextType !== ContextType.Skipped &&
          RESPONSE_GENERATOR_STEPS.includes(response.type) &&
          isConditionallyVisible(
            stepParamsWithStage as StepParamsForTypeWithStage<InteractionStepType.ModelResponseSelector>,
            responses,
            i,
            this.threadParams.templateVariables,
            this.reviewLevel,
          )
        );
      })
      .at(-1);

    return mostRecentResponseGeneratorStepInTurn?.output ?? '';
  };

  getStepsWithResponses() {
    let responses = [...this.responses];

    const turnLength = this.threadParams.turn.length + 1;
    const beforeLength = this.threadParams.before.length;

    let currentStage =
      responses.length < beforeLength ? ChatTaskParamsStages.Before : ChatTaskParamsStages.Turn;

    // Get before messages
    const beforeResponses = responses.slice(0, beforeLength);
    responses = responses.slice(beforeLength);

    // Start populating turns and after responses
    const turns: TurnStepsWithResponses[] = [];
    const afterResponses: IndexedInteractionStepResponse[] = [];

    while (responses.length > 0) {
      const turnSteps = this.createNewTurnSteps();
      const turnResponses = responses.slice(0, turnLength);

      responses = responses.slice(turnLength);

      // Possibility A: Turn is incomplete.
      if (turnResponses.length !== turnLength) {
        turns.push({
          steps: turnSteps,
          responses: turnResponses,
          complete: false,
          shouldContinue: true,
          messages: [],
        });
        continue;
      }
      const lastResponse = turnResponses[turnResponses.length - 1];
      const context = lastResponse.context as TurnContinuationContext;
      const assistantMessage: Message = {
        role: MessageRole.Assistant,
        content: context.message,
        attachments: context.modelResponseAttachments,
      };

      // Possibility B: Full chat has been terminated.
      // Remaining responses below to the after response.
      if (!context.shouldContinue) {
        turns.push({
          steps: turnSteps,
          responses: turnResponses,
          complete: true,
          shouldContinue: context.shouldContinue,
          messages: [assistantMessage],
        });
        afterResponses.push(...responses);
        break;
      }

      // Possibility C: Turn is complete, but chat is not.
      const userMessage: Message = {
        role: MessageRole.User,
        content: producePromptWithReferenceTexts(
          lastResponse,
          this.threadParams.useAppendedReferenceText,
        ),
        attachments: context.attachments,
      };
      turns.push({
        steps: turnSteps,
        responses: turnResponses,
        complete: true,
        shouldContinue: context.shouldContinue,
        messages: [assistantMessage, userMessage],
      });
    }

    // If the last turn is complete, but the chat is not, then we need to add one more turn with
    // zero responses.
    const lastTurn = turns[turns.length - 1];
    if (!lastTurn || (lastTurn.complete && lastTurn.shouldContinue)) {
      turns.push({
        steps: this.createNewTurnSteps(),
        responses: [],
        complete: false,
        shouldContinue: true,
        messages: [],
      });
    }
    if (lastTurn && lastTurn.complete && !lastTurn.shouldContinue) {
      currentStage = ChatTaskParamsStages.After;
    }
    const beforeSteps = this.threadParams.before;
    const afterSteps = this.threadParams.after;
    return {
      before: _.zip(beforeSteps, beforeResponses),
      turns,
      after: _.zip(afterSteps, afterResponses),
      currentStage,
    };
  }

  flattenStepsWithResponses(
    before: StepWithResponse[],
    turns: TurnStepsWithResponses[],
    after: StepWithResponse[],
  ): StepWithResponse[] {
    return [...before, ...turns.flatMap(turn => _.zip(turn.steps, turn.responses)), ...after];
  }

  getFlattenedStepsWithResponses(): StepWithResponse[] {
    const { before, turns, after } = this.getStepsWithResponses();
    return this.flattenStepsWithResponses(before, turns, after);
  }

  getChatHistory() {
    const { prevMessages: _prevMessages } = extractPrefilledMessages(
      this.threadParams.initialMessages,
    );
    const prevMessages = _prevMessages.map(message => ({
      role: message.role,
      content: message.content || '',
    }));

    const { before, turns } = this.getStepsWithResponses();
    if (!before?.length) {
      return [];
    }

    // Last before step + all turns
    const promptStepResponsePair = before.find(([, response]) => {
      return response && response.type === InteractionStepType.PromptInput;
    });
    if (!promptStepResponsePair) {
      return [];
    }
    const [, promptInputResponse] = promptStepResponsePair;
    if (!promptInputResponse) {
      return [];
    }
    const beforeStepMessage: Message = {
      role: MessageRole.User,
      content: producePromptWithReferenceTexts(
        promptInputResponse,
        this.threadParams.useAppendedReferenceText,
      ),
      attachments: promptInputResponse.context?.attachments,
    };
    const turnMessages = turns.flatMap(turn => turn.messages);
    const allMessages = [...prevMessages, beforeStepMessage, ...turnMessages];
    return allMessages;
  }

  getNumTurns() {
    const { turns } = this.getStepsWithResponses();
    return turns.length;
  }

  getMostRecentResponseForStepId(stepId: string) {
    for (let ii = this.responses.length - 1; ii >= 0; ii--) {
      const response = this.responses[ii];
      if (response.step_id === stepId) {
        return response;
      }
      // Break if we reach the beginning of the turn or the last turn
      if (
        response.type === InteractionStepType.PromptInput ||
        response.type === InteractionStepType.MultiTurnContinue
      ) {
        break;
      }
    }
    return null;
  }
}
